/*
  Subscription checkout choice (snippets/subscription-checkout.liquid).

  A click on a theme checkout button (cart drawer and cart page, button[name="checkout"]) is let through untouched while
  the cart holds no subscription. With one of the Progus plans in the cart it opens the dialog instead:
  - "local": clicks the Progus Mollie button next to that checkout button, which converts the cart for Mollie and
    goes to a checkout with only iDEAL and Bancontact (Kip hides Shopify Payments' Bancontact there).
  - "card": submits the theme's checkout as if the dialog had not been there, so the selling plan stays on the line
    and Shopify creates a native card subscription.

  The cart is read from /cart.js on load and after every cart change, so the click usually knows the answer already.
  When it does not, the click waits for one read and then decides.
*/

const dialog = document.getElementById('subscription-checkout');

if (dialog) {
  const CHECKOUT_SELECTOR = 'button[name="checkout"], input[name="checkout"]';
  const PROGUS_SELECTOR = '.progus-mollie-checkout';
  const PROGUS_WAIT_MS = 3000;
  const root = window.Shopify?.routes?.root || '/';

  let hasSubscription = null;
  let reading = null;
  let lastButton = null;
  let bypass = false;

  // The Progus embed renders the app's own plan ids; the snippet's list is the fallback
  const planIds = () => {
    const progus = document.querySelector('[data-progus-mollie-config]')?.dataset.ownPlanIds;
    return new Set((progus || dialog.dataset.planIds || '').split(',').map((id) => id.trim()).filter(Boolean));
  };

  const readCart = () => {
    reading = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(String(response.status)))))
      .then((cart) => {
        const ids = planIds();

        hasSubscription = cart.items.some((item) => {
          const planId = item.selling_plan_allocation?.selling_plan?.id;
          // A line Progus already converted for Mollie (customer came back from checkout) still counts
          return (planId && ids.has(String(planId))) || Boolean(item.properties?._progus_selling_plan_id);
        });

        return hasSubscription;
      })
      .catch((error) => {
        console.warn('[subscription-checkout] Could not read the cart.', error);
        hasSubscription = null;
        return null;
      });

    return reading;
  };

  // The theme's own checkout, exactly as without this dialog (the form also saves the order note)
  const proceed = (clicked) => {
    // A drawer re-render replaces the clicked button; its replacement does the same job
    const button = clicked?.isConnected ? clicked : document.querySelector(CHECKOUT_SELECTOR);
    bypass = true;

    try {
      if (button?.form && button.isConnected) {
        button.form.requestSubmit(button);
      } else {
        window.location.assign(`${root}checkout`);
      }
    } finally {
      bypass = false;
    }
  };

  // The Progus button that belongs to the clicked checkout button, or any on the page
  const findProgusButton = () => {
    const scope = lastButton?.parentElement;
    return scope?.querySelector(PROGUS_SELECTOR) || document.querySelector(PROGUS_SELECTOR);
  };

  const waitForProgusButton = () =>
    new Promise((resolve) => {
      const started = Date.now();

      const check = () => {
        const button = findProgusButton();

        if (button || Date.now() - started >= PROGUS_WAIT_MS) {
          resolve(button || null);
        } else {
          setTimeout(check, 100);
        }
      };

      check();
    });

  /*
    Placement, set on the dialog as CSS variables and data-context:
    - "drawer": the dialog covers exactly the cart drawer (its footer's left edge and width, full height), dims only the
      drawer and the choice slides up from the drawer's bottom edge.
    - "page" (cart page): a sheet from the bottom of the screen under the summary column with the checkout button, at
      least MIN_WIDTH wide and never past the screen edges.
    On a phone the stylesheet makes both full width.
  */
  const MIN_WIDTH = 360;
  const EDGE = 8;

  const anchorSheet = () => {
    const drawerColumn = lastButton?.closest('cart-drawer') ? lastButton.closest('form, .cart-drawer__footer-inner') : null;
    const column = drawerColumn || lastButton?.closest('[class*="cart__"], form') || lastButton;
    const rect = column?.getBoundingClientRect();

    dialog.dataset.context = drawerColumn ? 'drawer' : 'page';

    if (!rect || !rect.width) {
      dialog.style.removeProperty('--subscription-sheet-left');
      dialog.style.removeProperty('--subscription-sheet-width');
      return;
    }

    const viewport = document.documentElement.clientWidth;
    let width = rect.width;
    let left = rect.left;

    if (!drawerColumn) {
      width = Math.min(Math.max(rect.width, MIN_WIDTH), viewport - EDGE * 2);
      left = Math.min(Math.max(rect.left + rect.width / 2 - width / 2, EDGE), viewport - width - EDGE);
    }

    dialog.style.setProperty('--subscription-sheet-left', `${left}px`);
    dialog.style.setProperty('--subscription-sheet-width', `${width}px`);
  };

  /*
    The cart drawer closes on any click outside itself (the theme's focus trap), and a click on this sheet counted as
    outside while the sheet lived at the end of <body>: choosing an option or closing the sheet also closed the drawer.
    So the sheet is moved into the drawer while it opens from there, and back to <body> for the cart page. As a modal
    dialog it is drawn in the top layer either way, so the move does not change where it shows.
  */
  const placeDialog = () => {
    const home = lastButton?.closest('cart-drawer') || document.body;

    if (dialog.parentElement !== home) {
      home.append(dialog);
    }
  };

  // Whether the sheet should be on screen. The drawer re-renders by replacing all its children (every cart change,
  // cart:refresh), which takes the sheet out of the page without a close event; this flag survives that.
  let sheetWanted = false;

  const open = () => {
    if (!dialog.open) {
      placeDialog();
      anchorSheet();
      dialog.showModal();
      sheetWanted = true;
    }
  };

  dialog.addEventListener('close', () => {
    sheetWanted = false;
  });

  /*
    Put the sheet back when a drawer re-render took it out while it was open. The removal leaves the open attribute
    behind without the modal state, so the attribute is cleared (no close event) before the sheet is shown again,
    anchored to the re-rendered checkout button.
  */
  document.addEventListener('cart-drawer:refreshed', (event) => {
    if (!sheetWanted || dialog.isConnected) {
      return;
    }

    lastButton = event.target.querySelector(CHECKOUT_SELECTOR) || lastButton;
    dialog.removeAttribute('open');
    event.target.append(dialog);
    anchorSheet();
    dialog.showModal();
  });

  window.addEventListener('resize', () => {
    if (dialog.open) {
      anchorSheet();
    }
  });

  const close = () => {
    if (dialog.open) {
      dialog.close();
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.(CHECKOUT_SELECTOR);

      if (!button || bypass || hasSubscription === false) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      lastButton = button;

      const decided = hasSubscription === true ? Promise.resolve(true) : reading || readCart();

      decided.then((subscription) => {
        if (subscription) {
          open();
        } else {
          proceed(button);
        }
      });
    },
    true
  );

  /*
    Loading state: the choice stays on screen with a spinner on the chosen option until the browser leaves for checkout.
    The iDEAL route takes about two seconds before it leaves (Progus converts the cart line by line), and a sheet that
    closed straight away left the customer looking at an unchanged cart for that time.
  */
  const BUSY_TIMEOUT_MS = 15000;
  let busyTimer = null;

  const setBusy = (option) => {
    clearTimeout(busyTimer);
    dialog.toggleAttribute('data-busy', Boolean(option));
    dialog.querySelectorAll('[data-subscription-checkout-pay]').forEach((item) => {
      item.toggleAttribute('aria-busy', item === option);
      item.disabled = Boolean(option);
    });

    // Still here long after the click (a failed conversion, a blocked navigation): give the choice back
    if (option) {
      busyTimer = setTimeout(() => setBusy(null), BUSY_TIMEOUT_MS);
    }
  };

  dialog.addEventListener('cancel', (event) => {
    // Escape does not close the sheet while the customer is on the way to checkout
    if (dialog.hasAttribute('data-busy')) {
      event.preventDefault();
    }
  });

  dialog.addEventListener('click', (event) => {
    if (dialog.hasAttribute('data-busy')) {
      return;
    }

    // A click on the backdrop lands on the dialog element itself
    if (event.target === dialog || event.target.closest('[data-subscription-checkout-close]')) {
      close();
      return;
    }

    const option = event.target.closest('[data-subscription-checkout-pay]');

    if (!option) {
      return;
    }

    setBusy(option);

    if (option.dataset.subscriptionCheckoutPay === 'card') {
      proceed(lastButton);
      return;
    }

    waitForProgusButton().then((progusButton) => {
      if (progusButton) {
        progusButton.click();
      } else {
        console.warn('[subscription-checkout] No Progus Mollie button found, going to the standard checkout.');
        proceed(lastButton);
      }
    });
  });

  // Until the new cart is read, a click waits for it instead of trusting the old answer
  const cartChanged = () => {
    hasSubscription = null;
    readCart();
  };

  ['cart:change', 'cart:refresh'].forEach((name) => document.addEventListener(name, cartChanged));
  /*
    Back from checkout: the Progus embed turns the Mollie marker lines back into subscription lines (remove, then add)
    while the theme re-renders the cart drawer for the restored page. The theme can catch the cart in between, empty,
    and keep showing "empty" although the cart is not. So once the cart holds no markers and has stopped changing,
    the drawer and cart count are refreshed one more time. On a normal page load this only happens when there were
    markers to revert.
  */
  const SETTLE_TIMEOUT_MS = 8000;
  const SETTLE_INTERVAL_MS = 500;
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const hasMarkers = (cart) =>
    cart.attributes?._progus_mollie_subscription === 'true' ||
    cart.items.some((item) => Boolean(item.properties?._progus_selling_plan_id));

  const cartSignature = (cart) => `${cart.item_count}|${cart.items.map((item) => item.key).join(',')}`;

  const refreshAfterProgus = async (restored) => {
    const started = Date.now();
    let sawMarkers = false;
    let previous = null;

    try {
      while (Date.now() - started < SETTLE_TIMEOUT_MS) {
        const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
        const signature = cartSignature(cart);

        if (hasMarkers(cart)) {
          sawMarkers = true;
        } else if (!restored && !sawMarkers) {
          // A normal page load with nothing to revert: one read, no polling
          return;
        } else if (signature === previous) {
          break;
        }

        previous = signature;
        await wait(SETTLE_INTERVAL_MS);
      }
    } catch (error) {
      console.warn('[subscription-checkout] Could not read the cart after returning from checkout.', error);
    }

    if (restored || sawMarkers) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      // Back from checkout through the back button: the page comes back as it was left, sheet and spinner included
      setBusy(null);
      close();
      readCart();
      refreshAfterProgus(true);
    }
  });

  readCart();
  refreshAfterProgus(false);
}
